/* eslint-disable max-lines */
import {
  isChooseNoteStructureModalOpenAtom,
  type PostTranscriptionEntryMode,
  postTranscriptionEntryModeAtom,
} from '@/atoms/noteStructureAtoms';
import { activePanelAtom, embeddingsPanelSessionIdAtom } from '@/atoms/panelAtoms';
import {
  getTauriLastUsedInputDevice,
  setTauriLastUsedInputDevice,
  setTauriLastUsedOutputDevice,
  setTauriLastUsedOutputDeviceDisplayName,
  tauriDictationInputDevicePreferenceAtom,
  tauriLastUsedOutputDeviceAtom,
  tauriLastUsedOutputDeviceDisplayNameAtom,
} from '@/atoms/persistedPreferencesAtoms';
import {
  errorStartingRecordingAtom,
  shouldShowSocketErrorModal,
} from '@/atoms/sessionAtoms';
import {
  GET_EVIDENCE_DOCUMENT_SUGGESTION_EXPERIMENT_VARIANT,
  GET_POST_TRANSCRIPTION_NOTE_ENTRY_VARIANT,
  IS_CHUNK_RECORDING_ENABLED,
  IS_CLINICAL_CODING_ENABLED,
  IS_CLINICAL_TASKS_ENABLED,
  IS_EVIDENCE_CENTRAL_SEARCH_BAR_ENABLED,
  IS_POST_TRANSCRIPTION_NOTE_ENTRY_ASSIGNED,
  IS_TASK_GENERATION_BG_AUTOGEN_ENABLED,
  POST_TRANSCRIPTION_NOTE_ENTRY_FLAG_KEY,
  USE_RECORDING_V2,
} from '@/features/flags';
import { DEFAULT_TEMPLATE_AUTO_MODE } from '@/features/settings/defaults/constants';
import { isShadowHeidiEnabled } from '@/features/shadowHeidi/hooks/useIsShadowHeidiEnabled';
import {
  trackQueuedDocumentGenerationFailed,
  trackQueuedDocumentGenerationStarted,
} from '@/lib/analytics/scribe/documents';
import {
  noteAutoRetryDecided,
  trackFailedToStartChunkRecording,
} from '@/lib/analytics/scribe/general';
import {
  sessionResumeRecording,
  sessionStartRecording,
  sessionStopRecording,
} from '@/lib/analytics/scribe/session';
import {
  SYSTEM_DEFAULT_DEVICE_MARKER,
  SYSTEM_WIDE_DEVICE_MARKER,
} from '@/tauri/constants';
import { setDictationInputDevice } from '@/tauri/dictation/api';
import { toast } from '@heidi/design-system/toast';

import {
  getIsInSessionNudgesChenmedEnabled,
  getIsMedicationHighlightsEnabled,
  getWindowsCommsDefaultMicGate,
  getWindowsSystemAudioCaptureGate,
  IS_FIRST_NOTE_TEMPLATE_SELECTION_ENABLED,
  IS_HAIKU_NOTE_PICKER_DIRECT_DISPATCH_ENABLED,
  isDictateSeparateMicrophoneEnabled,
  isNoteGenerationRecoveryEnabled,
} from '@/features/flags';
import { templateSelectionModalOpenAtom } from '@/features/growth/first-note-templates/atoms';
import { isFirstNoteUser } from '@/features/growth/first-note-templates/isFirstNoteUser';
import {
  legacyStreamResponseBody,
  type LegacyStreamResponseInput,
} from '@/features/note-generation/stream-response';
import { commitFailedGeneration } from '@/features/note-generation/stream-response/effects/commit-failed-generation';
import { captureGenerationAttemptBaseline } from '@/features/note-generation/stream-response/recovery/attempt-outcome';
import { readAutoRetryEnv } from '@/features/note-generation/stream-response/recovery/auto-retry-env';
import { runWithAutoRetry } from '@/features/note-generation/stream-response/recovery/run-with-auto-retry';
import { extractPatientProfileContextFromTranscript } from '@/features/patientContext/utils';
import {
  logCernerEmbeddedRecordButtonGetUserMediaResult,
  runCernerEmbeddedRecordButtonDiagnostics,
} from '@/integrations/sof-launch/cerner-embedded-diagnostics';
import { armDictateReengagementCard } from '@heidi/dictate/data';
import { isWindows } from '@heidi/utils';

import { scheduleMedicationDetectionsRefetchAfterNoteComplete } from '@/components/tiptap/shared/medication-highlights/schedule-refetch-after-note-complete';
import { getDomains } from '@/helpers/getDomains';
import { getPersonalizationPreference } from '@/helpers/getPersonalizationPreference';
import {
  generateClinicalTasksForSession,
  generateClinicalTasksWithExclusionsForSession,
  scheduleClinicalTasksRefetchAfterBackgroundAutogen,
} from '@/helpers/panels/panel';
import {
  GET_DOCUMENT_BY_ID,
  invalidateDocumentQuery,
  type QueryDocumentResponse,
} from '@/hooks/query/useQueryDocumentById';
import { GET_DOCUMENTS_BY_ID } from '@/hooks/query/useQueryDocumentsBySessionId';
import { invalidateGamifyTrialExtension } from '@/hooks/query/useQueryGamifyTrialExtension';
import { invalidatePatientRecap } from '@/hooks/query/useQueryPatientRecap';

import { getCurrentUser } from '@/hooks/query/useQueryUser';
import { enableNoSleep } from '@/lib/noSleep';
import { classifyDocumentGenerateError } from '@/lib/telemetry/documentGenerateTracking';
import { type NoteGenerateTrigger } from '@/lib/telemetry/noteGenerateTracking';
import {
  intentionalCloseCodesMapping,
  websocketBECloseCodes,
  websocketSocketIntentionalCloseCodes,
} from '@/lib/utils';
import { getDocumentsBySessionId } from '@/pages/api/scribeAPI';
import { generateDocument, getPinnedTemplates } from '@/pages/api/scribeAPIV2';
import { store } from '@/store';
import { isProcessDevice } from '@/tauri/audioDeviceUtils';
import { recorderFmc } from '@/tauri/controller/recording';
import { isTauri } from '@/tauri/utils';
import audioRecorder from '@/utils/audioRecorder';
import { mapNewBrain } from '@/utils/brainUtils';
import chunkAudioRecorder, { isUsingGeminiTranscriber } from '@/utils/chunkAudioRecorder';
import {
  getSessionFromCache,
  updateConsultNoteStreamStatus,
} from '@/utils/consultNoteStreamHelpers';
import { getAuthTokenForHttpClientSync } from '@/utils/http/getAuthTokenForHttpClientSync';
import { appendWorkspaceIdParam } from '@/utils/http/workspaceWebsocketParam';
import { logger } from '@/utils/logger/logger';
import { isOfflineLocalSession } from '@/utils/offline-recording/localSessionSettings';
import { sanitizeError } from '@heidi/runtime/logger';
import {
  type Brain,
  type ContentType,
  DictationCleanupModes,
  DocumentGenType,
  GenerationMethod,
  RecordingStatus,
  SidePanelState,
  TranscribeMode,
  VoiceStyle,
} from '@heidi/schema/ml-scribe';
import {
  type CustomStyle,
  GET_SESSION_BY_ID,
  type GetDocumentsResponse,
  type QuerySessionResponse,
  type SessionResponse,
  StreamingStatus,
} from '@heidi/session/contracts';
import { isTranscriptDeletedInCache } from '@heidi/session/data';
import i18next from 'i18next';

import offlineAudioRecorder from '@/utils/offlineAudioRecorder';
import { reactQueryClient } from '@/utils/reactQueryClient';

import * as amplitude from '@amplitude/analytics-browser';
import { createModel } from '@rematch/core';
import { type IMediaRecorder } from 'extendable-media-recorder';
import { getDefaultStore } from 'jotai';
import { isAndroid } from 'react-device-detect';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { type AudioDevice } from 'src-tauri/src/bindings/AudioDevice';
import { type AvailableDevices } from 'src-tauri/src/bindings/AvailableDevices';
import { type RootModel } from '.';

const jotaiStore = getDefaultStore();

const DOCUMENT_GENERATION_TYPES = new Set<DocumentGenType>([
  DocumentGenType.TEMPLATE,
  DocumentGenType.INTERNAL_TEMPLATE,
  DocumentGenType.PRESET_AI_COMMAND,
  DocumentGenType.ASK_AI,
  DocumentGenType.GPCCMP,
  DocumentGenType.PDF,
]);

export const USE_CHUNK_RECORDING = () => {
  if (isUsingGeminiTranscriber()) {
    return true;
  }
  return IS_CHUNK_RECORDING_ENABLED();
};

// enum follows backend
export const enum RecordingMode {
  DICTATION = 'DICTATE',
  TRANSCRIPTION = 'TRANSCRIBE',
}

// StreamingStatus lives outside this model — now in `@heidi/session/contracts`
// — so files the model itself imports (notably
// src/features/note-generation/stream-response/**) can reference it without
// re-entering the model and creating a circular import. See
// docs/design-docs/stream-response-refactor.md §11 R1. Re-exported here for
// backwards compatibility with the existing
// `import { StreamingStatus } from '@/models/scribeRecordModel'` callers.
export { StreamingStatus } from '@heidi/session/contracts';

export type InitialState = {
  activeSocket: {
    socket: ReconnectingWebSocket | null;
    sessionID: string | null;
    lastReceivedHeartbeat: number | null;
    hasBeenMarkedError?: boolean;
  };
  ownMediaRecorder: IMediaRecorder | null;
  mediaStream?: MediaStream | null;
  tauriSessionFiles: {
    sessionId: string;
    files: {
      name: string;
      size: number;
      bytes: number;
    }[];
  } | null;

  offlineRecordingStartTime: number;

  androidBackgroundAudioPlayer: HTMLAudioElement | null;
  // streamingStatus: StreamingStatus;
  browserLiveTranscription: string | null;

  tauriInputDeviceSelected?: AudioDevice;
  tauriOutputDeviceSelected?: AudioDevice;
  tauriInputDevices?: AudioDevice[];
  tauriOutputDevices?: AudioDevice[];
};

const initialState: InitialState = {
  activeSocket: {
    socket: null,
    sessionID: null,
    lastReceivedHeartbeat: null,
  },
  tauriSessionFiles: null,
  ownMediaRecorder: null,
  offlineRecordingStartTime: 0,
  // lastReceivedHeartbeat: null,
  // streamingStatus: StreamingStatus.NOT_STARTED,

  androidBackgroundAudioPlayer: null,

  browserLiveTranscription: null,
};

/**
 * `direction` is the single discriminator both direction-dependent behaviours
 * derive from, so an inconsistent combination cannot be written at all.
 *
 * Two things hang off it, and they point opposite ways: the process-fallback
 * `lastUsedDisplayName` is output-only, while the first-run sentinel may count
 * a communications-default device on input lists only. Carrying them as two
 * independent optional flags left `{ lastUsedDisplayName, commsDefaultEligible:
 * true }` — a list that is somehow both — type-checking, and left the output
 * call site relying on a *comment* about an absent argument.
 *
 * Why the comms term is input-only: the gate it reads is the comms-default
 * *mic* flag and the start-time validity guard this branch mirrors is
 * input-side. Render endpoints carry the mark too, and their `is_default` is an
 * exact-name compare against cpal's console default while the mark uses the
 * tolerant friendly-name matcher — so an output list with a comms mark and no
 * `is_default` is reachable, and counting it would flip the persisted output
 * selection to the sentinel.
 */
export type FindActiveTauriDevicePayload = {
  devices: AudioDevice[];
  lastUsedDeviceName: string | undefined;
} & (
  | { direction: 'input' }
  | {
      direction: 'output';
      /** Display name for process fallback when the PID changes between sessions. */
      lastUsedDisplayName?: string | undefined;
    }
);

export const scribeRecordModel = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setTauriSessionFiles(state, payload: InitialState['tauriSessionFiles']) {
      return { ...state, tauriSessionFiles: payload };
    },
    setTauriInputDeviceSelected(state, payload: AudioDevice) {
      return { ...state, tauriInputDeviceSelected: payload };
    },
    setTauriOutputDeviceSelected(state, payload: AudioDevice) {
      return { ...state, tauriOutputDeviceSelected: payload };
    },
    setTauriInputDevices(state, payload: AudioDevice[]) {
      return { ...state, tauriInputDevices: payload };
    },
    setTauriOutputDevices(state, payload: AudioDevice[]) {
      return { ...state, tauriOutputDevices: payload };
    },
    setBrowserLiveTranscription(state, payload: string | null) {
      return { ...state, browserLiveTranscription: payload };
    },

    setLastReceivedHeartbeat(state, payload: number | null) {
      return {
        ...state,
        activeSocket: {
          ...state.activeSocket,
          lastReceivedHeartbeat: payload,
        },
      };
    },
    initializeSocket(
      state,
      payload: { socket: ReconnectingWebSocket; sessionID: string }
    ) {
      return {
        ...state,
        activeSocket: {
          socket: payload.socket,
          sessionID: payload.sessionID,
          lastReceivedHeartbeat: Date.now(),
          hasBeenMarkedError: false,
        },
      };
    },
    clearSocket(state) {
      return {
        ...state,
        activeSocket: { socket: null, sessionID: null, lastReceivedHeartbeat: null },
      };
    },
    // setSocket(state, payload: ReconnectingWebSocket | null) {
    //   return { ...state, socket: payload };
    // },
    setMediaStream(state, payload: MediaStream | null) {
      return { ...state, mediaStream: payload };
    },
    setOwnMediaRecorder(state, payload: IMediaRecorder | null) {
      return { ...state, ownMediaRecorder: payload };
    },
    setOfflineRecordingStartTime(state, payload: number) {
      return { ...state, offlineRecordingStartTime: payload };
    },
    resetOfflineRecording(state) {
      return { ...state, offlineRecordingStartTime: Date.now() };
    },
    setAndroidBackgroundAudioPlayer(state, payload: HTMLAudioElement | null) {
      return { ...state, androidBackgroundAudioPlayer: payload };
    },

    setHasBeenMarkedError(state, payload: boolean) {
      return {
        ...state,
        activeSocket: { ...state.activeSocket, hasBeenMarkedError: payload },
      };
    },

    //   return { ...state, streamingStatus: payload };
    // },
  },
  effects: (dispatch) => ({
    // !Error: need to call update duration api when stop recording, cannot use hooks here
    // async updateSessionDurationById({ sessionId }: { sessionId: string }) {
    //   try {
    //     await updateSessionById(sessionId, {
    //       duration: store.getState().timerModel.seconds,
    //     });
    //     await reactQueryClient.invalidateQueries({
    //       queryKey: [GET_SESSION_BY_ID, { sessionId: sessionId }],
    //     });
    //   } catch (error) {}
    // },
    setTauriInputDevice(payload: AudioDevice) {
      dispatch.scribeRecordModel.setTauriInputDeviceSelected(payload);
      // Persist the selected device
      setTauriLastUsedInputDevice(payload.name);
      // Push the selection to the Rust dictation recorder so the hotkey-driven
      // dictation flow captures from the same mic the user picked here (SCR-4017).
      // SCR-6773: when the separate-mic flag is on and the user has an explicit
      // Dictate preference, leave native Dictate alone — preference owns it.
      if (isDictateSeparateMicrophoneEnabled()) {
        const preference = getDefaultStore().get(tauriDictationInputDevicePreferenceAtom);
        if (preference !== undefined) {
          return;
        }
      }
      // The System Default sentinel maps to `null` so Rust tracks the OS default.
      const dictationDeviceName =
        payload.name === SYSTEM_DEFAULT_DEVICE_MARKER ? null : payload.name;
      void setDictationInputDevice(dictationDeviceName).catch((e) => {
        logger.error('Failed to push dictation input device to Rust', e);
      });
    },

    setTauriOutputDevice(payload: AudioDevice) {
      dispatch.scribeRecordModel.setTauriOutputDeviceSelected(payload);
      // Persist device name for matching (PID for process, device name for speaker).
      setTauriLastUsedOutputDevice(payload.name);
      // For process devices, also persist display name for fallback when PID changes between sessions.
      if (isProcessDevice(payload)) {
        setTauriLastUsedOutputDeviceDisplayName(payload.display_name ?? payload.name);
      } else {
        setTauriLastUsedOutputDeviceDisplayName(undefined);
      }
    },

    findActiveTauriDevice(payload: FindActiveTauriDevicePayload) {
      const { devices, lastUsedDeviceName, direction } = payload;
      const lastUsedDisplayName =
        direction === 'output' ? payload.lastUsedDisplayName : undefined;

      // If user previously selected "System Default", return that
      if (lastUsedDeviceName === SYSTEM_DEFAULT_DEVICE_MARKER) {
        return {
          name: SYSTEM_DEFAULT_DEVICE_MARKER,
          is_default: true,
          display_name: null,
          icon_data_url: null,
          is_communications_default: false,
        };
      }

      // Restore a persisted "System audio" (whole-system capture) selection
      // while the flag is armed on Windows; flag OFF (kill switch) degrades
      // to System Default. 'unknown' (flag provider not hydrated yet)
      // preserves the selection instead of persisting a downgrade — the
      // start-time resolver enforces what reaches Rust.
      if (lastUsedDeviceName === SYSTEM_WIDE_DEVICE_MARKER) {
        if (isWindows && getWindowsSystemAudioCaptureGate() !== 'off') {
          return {
            name: SYSTEM_WIDE_DEVICE_MARKER,
            is_default: false,
            display_name: null,
            icon_data_url: null,
            is_communications_default: false,
          };
        }
        return {
          name: SYSTEM_DEFAULT_DEVICE_MARKER,
          is_default: true,
          display_name: null,
          icon_data_url: null,
          is_communications_default: false,
        };
      }

      // If no preference exists (new user), pre-select "System Default".
      // Unless the comms-default flag is known-OFF, a communications-default
      // input also counts as "a default exists" — mirroring the start-time
      // validity guard. Otherwise a first-run machine with no console default
      // would persist "first device" as an explicit selection, permanently
      // opting the user out of the smart default's sentinel population.
      // 'unknown' (provider not hydrated) keeps the sentinel eligible: that
      // choice is recoverable, the fallthrough persist is not, and the
      // awaited start-time gate enforces the real flag.
      if (!lastUsedDeviceName) {
        const commsMicGate = getWindowsCommsDefaultMicGate();
        const commsCounts = direction === 'input' && commsMicGate !== 'off';
        const hasSentinelEligibleDefault = devices.some(
          (d) => d.is_default || (commsCounts && d.is_communications_default)
        );
        if (hasSentinelEligibleDefault) {
          return {
            name: SYSTEM_DEFAULT_DEVICE_MARKER,
            is_default: true,
            display_name: null,
            icon_data_url: null,
            is_communications_default: false,
          };
        }
      }

      // Try exact name match first (same PID for process, exact device name for speaker)
      let selectedDevice = devices.find((device) => device.name === lastUsedDeviceName);

      // For process devices: if PID match failed, try display_name (e.g. "Opera") for fallback
      // when the app restarted and PID changed.
      if (!selectedDevice && lastUsedDisplayName) {
        selectedDevice = devices.find(
          (device) => device.display_name === lastUsedDisplayName
        );
      }

      // Legacy: lastUsedDeviceName might be a display name from before we persisted both
      if (!selectedDevice) {
        selectedDevice = devices.find(
          (device) => device.display_name === lastUsedDeviceName
        );
      }

      // Fall back to default device if preferred device not found
      if (!selectedDevice) {
        selectedDevice = devices.find((device) => device.is_default);
      }

      // Fall back to first device if no default found
      if (!selectedDevice && devices.length > 0) {
        selectedDevice = devices[0];
      }

      return selectedDevice;
    },

    /**
     * Refreshes the device lists. Returns the RAW eligible output devices from
     * this enumeration (`undefined` on failure): the stored
     * `tauriOutputDevices` list retains the currently selected process even
     * when the OS stops returning it (anti-flicker, see the merge below), so
     * callers validating that an app process is actually alive (silent-capture
     * silent-capture fixes) must match against this return value, never the
     * merged Redux list.
     */
    async getTauriDevices(params?: {
      updateSelectedDevices?: boolean;
      /**
       * A device list already produced by the Rust side (the
       * `audio:device_list_changed` payload). When present, applied directly
       * instead of re-invoking enumeration — the Windows walk (WASAPI
       * process-audio + icons) already ran once to detect the change.
       */
      prefetchedDevices?: AvailableDevices;
    }): Promise<AudioDevice[] | undefined> {
      try {
        const { input_devices, output_devices } =
          params?.prefetchedDevices ?? (await recorderFmc.get_available_audio_devices());
        dispatch.scribeRecordModel.setTauriInputDevices(input_devices);

        const eligibleOutputDevices = output_devices;

        // Merge output devices: never clear the list. Only remove devices no longer
        // returned by the API, add new ones, and refresh data for existing ones.
        // Keep the currently selected process in the list even when temporarily missing
        // from the API to prevent Apps section flicker and selection reverting to speaker.
        const currentInputDevice =
          store.getState().scribeRecordModel.tauriInputDeviceSelected;
        const currentOutputDevices =
          store.getState().scribeRecordModel.tauriOutputDevices ?? [];
        const currentOutputDevice =
          store.getState().scribeRecordModel.tauriOutputDeviceSelected;
        const apiByName = new Map(eligibleOutputDevices.map((d) => [d.name, d]));
        const resultNames = new Set<string>();

        const merged: AudioDevice[] = [];
        for (const d of currentOutputDevices) {
          const inApi = apiByName.has(d.name);
          const isSelectedProcess =
            currentOutputDevice &&
            isProcessDevice(currentOutputDevice) &&
            d.name === currentOutputDevice.name;
          if (inApi) {
            merged.push(apiByName.get(d.name)!);
            resultNames.add(d.name);
          } else if (isSelectedProcess) {
            merged.push(d);
            resultNames.add(d.name);
          }
        }
        for (const d of eligibleOutputDevices) {
          if (!resultNames.has(d.name)) {
            merged.push(d);
            resultNames.add(d.name);
          }
        }

        dispatch.scribeRecordModel.setTauriOutputDevices(merged);

        if (params?.updateSelectedDevices === false) {
          return eligibleOutputDevices;
        }

        const selectedInputDevice = dispatch.scribeRecordModel.findActiveTauriDevice({
          devices: input_devices,
          lastUsedDeviceName: getTauriLastUsedInputDevice(),
          direction: 'input',
        });

        const selectedOutputDevice = dispatch.scribeRecordModel.findActiveTauriDevice({
          devices: merged,
          direction: 'output',
          lastUsedDeviceName: getDefaultStore().get(tauriLastUsedOutputDeviceAtom),
          lastUsedDisplayName: getDefaultStore().get(
            tauriLastUsedOutputDeviceDisplayNameAtom
          ),
        });

        // An unchanged main-window selection must not overwrite a mic picked in
        // the dictation widget.
        if (
          selectedInputDevice &&
          selectedInputDevice.name !== currentInputDevice?.name
        ) {
          dispatch.scribeRecordModel.setTauriInputDevice(selectedInputDevice);
        }
        // Only update the output device when it has actually changed to avoid unnecessary
        // Redux re-renders and prevent the selection from jumping back to the default
        // headset if a process briefly disappears from the WASAPI active-session list.
        if (
          selectedOutputDevice &&
          selectedOutputDevice.name !== currentOutputDevice?.name
        ) {
          dispatch.scribeRecordModel.setTauriOutputDevice(selectedOutputDevice);
        }
        return eligibleOutputDevices;
      } catch (error) {
        logger.error('error getting tauri devices', error);
        return undefined;
      }
    },

    async startRecordingButtonPressed({
      sessionId,
      transcribeMode,
      onStop,
    }: {
      sessionId: string;
      transcribeMode: TranscribeMode;
      onStop?: () => void;
    }) {
      if (isTauri()) {
        // void dispatch.tauriRecordModel.startTauriRecording({
        //   session,
        // });
        return;
      }

      void enableNoSleep();
      logger.info('start recording button pressed');

      const isEmbeddedInHostFrame =
        typeof window !== 'undefined' && window.self !== window.top;
      if (isEmbeddedInHostFrame) {
        runCernerEmbeddedRecordButtonDiagnostics();
      }

      try {
        await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        if (isEmbeddedInHostFrame) {
          logCernerEmbeddedRecordButtonGetUserMediaResult({ ok: true });
        }
      } catch (error) {
        if (isEmbeddedInHostFrame) {
          logCernerEmbeddedRecordButtonGetUserMediaResult({
            ok: false,
            errorName: error instanceof Error ? error.name : 'UnknownError',
          });
        }
        jotaiStore.set(errorStartingRecordingAtom, () => {
          return {
            isOpen: true,
            error:
              'Please allow microphone access in your browser and refresh to start recording',
          };
        });
        if (onStop) {
          onStop();
        }
        return;
      }

      // logger.debug('startRecordingButtonPressed', sessionID, recordingMode);
      if (sessionId) {
        try {
          dispatch.scribeRecordModel.startStreamRecording({
            sessionID: sessionId,
            onStop,
          });

          const session = reactQueryClient.getQueryData<QuerySessionResponse>([
            GET_SESSION_BY_ID,
            { sessionId: sessionId },
          ]);
          const seconds = store.getState().timerModel.seconds;
          if (seconds > 0) {
            sessionResumeRecording({
              sessionId,
              isRecordingV2Enabled: USE_RECORDING_V2(),
              isTabSharing: session?.is_tab_sharing ?? false,
            });
          } else {
            const selectedDevice = store.getState().audio.selectedAudioSource;
            sessionStartRecording({
              sessionId,
              transcribeMode,
              device: selectedDevice?.label,
              deviceId: selectedDevice?.deviceId,
            });
          }
        } catch (error) {
          logger.error({
            event_type: 'START_STREAM_RECORDING_ERROR',
            details: {
              errorName: error instanceof Error ? error.name : typeof error,
              ...sanitizeError(error),
            },
          });
          if (onStop) {
            onStop();
          }
        }
      }

      if (!USE_CHUNK_RECORDING()) {
        void dispatch.audio.startVolumeDetection();
      }
    },

    startStreamRecording({
      sessionID,
      onStop,
      disableChunkRecording,
    }: {
      sessionID: string;
      onStop?: () => void;
      disableChunkRecording?: boolean;
    }) {
      try {
        // Read inside the try: the module now sits in a static import cycle
        // through `@/lib/auth`, and an init-order throw outside the try would
        // make the Record button a silent no-op instead of hitting the catch
        // below (which logs and calls the caller's onStop).
        const token = getAuthTokenForHttpClientSync();
        const session_id = sessionID;

        const session = reactQueryClient.getQueryData<QuerySessionResponse>([
          GET_SESSION_BY_ID,
          { sessionId: session_id },
        ]);

        if (!session) {
          throw new Error('Session not found');
        }

        logger.debug('st:sr: ' + session_id);

        const wsDomain = getDomains().websocketV1Url;

        // MOBILE-PARITY-DEBT(MUL-3395, APP-10164): the backend cannot infer the
        // active workspace (same reason HTTP sends Heidi-Workspace-Id), and
        // MUL-3395 pins only what a socket asserts — so a handshake omitting
        // this writes workspace_id: null. iOS/Android do not send it yet;
        // mobile mirror APP-10164. Remove when they do.
        const liveTranscribeAddress = appendWorkspaceIdParam(
          `${wsDomain}/api/v1/ml-scribe/sessions/${session_id}/transcribe-live?${
            token ? `token=${token}` : ''
          }`
        );

        const address = liveTranscribeAddress;

        const existingSocket = store.getState().scribeRecordModel.activeSocket.socket;
        if (existingSocket) {
          logger.debug('existing socket found, closing');
          // make sure to close and clear
          existingSocket?.close();
          dispatch.scribeRecordModel.clearSocket();
        }

        // play audio only if it's Android
        if (isAndroid) {
          try {
            const audio = new Audio();
            dispatch.scribeRecordModel.setAndroidBackgroundAudioPlayer(audio);
            // audio.src =
            //   'https://github.com/anars/blank-audio/raw/master/1-hour-and-20-minutes-of-silence.mp3';

            // audio.src =
            //   'https://filesamples.com/samples/audio/mp3/Symphony%20No.6%20(1st%20movement).mp3';
            audio.src = '/assets/mp3/1-hour-and-20-minutes-of-silence.mp3';

            void audio.play();

            navigator.mediaSession.metadata = new MediaMetadata({
              title: 'Heidi is listening',
            });
          } catch (error) {
            console.error(error);
          }
        }

        dispatch.scribeRecordModel.resetOfflineRecording();

        jotaiStore.set(shouldShowSocketErrorModal, () => false);
        dispatch.timerModel.onStart(session?.duration || 0);
        void dispatch.scribeRecordModel.startBackupOfflineRecording();
        // const reconnectingSocketOptions = {
        //   maxRetries: 10,
        //   debug: true,
        // };

        if (USE_CHUNK_RECORDING() && !disableChunkRecording) {
          void dispatch.scribeRecordModel.startChunkRecording(session_id);
          return;
        }

        const socket = new ReconnectingWebSocket(address);
        dispatch.scribeRecordModel.initializeSocket({ socket, sessionID: session_id });

        // The retry cap below lives in `onopen`, so it never fires on a
        // handshake that never opened — and the backend refuses one before
        // `accept()` when the asserted workspace is not the caller's, which the
        // browser reports as 1006. `onclose` applies the same cap through this
        // flag so a refused handshake cannot retry forever.
        let hasSocketOpened = false;

        socket.onopen = () => {
          hasSocketOpened = true;
          logger.debug('socket retry count: ' + socket.retryCount);
          if (socket.retryCount > 10) {
            logger.debug('socket retry count exceeds limit + sessionID', sessionID);
            socket?.close();
            if (onStop) {
              onStop();
            }
            return;
          }
          if (socket.retryCount > 5) {
            void dispatch.scribeRecordModel._markSocketError({
              sessionId: session_id,
            });
          }
          logger.debug('socket open + sessionID', sessionID);
          void dispatch.scribeRecordModel.handleSocketOpen();
        };

        socket.onmessage = () => {
          // logger.debug('socket message received');
          dispatch.scribeRecordModel.setLastReceivedHeartbeat(Date.now());
        };

        socket.onclose = (ev) => {
          // backend closes connection, close socket gracefully
          if (websocketBECloseCodes.includes(ev.code)) {
            logger.debug(
              'onclose: ' + store.getState().scribeRecordModel.activeSocket.sessionID
            );
            logger.debug(
              'socket closed by backend code: ' + ev.code + ' reason: ' + ev.reason
            );
            socket.close();

            if (websocketSocketIntentionalCloseCodes.includes(ev.code)) {
              jotaiStore.set(errorStartingRecordingAtom, () => {
                return {
                  isOpen: true,
                  error: intentionalCloseCodesMapping[ev.code] || 'Unknown error',
                };
              });
              dispatch.timerModel.onPause();
              audioRecorder?.stop();
              if (onStop) {
                onStop();
              }
              return;
            }
          } else if (!hasSocketOpened && socket.retryCount > 10) {
            // A drop after the socket opened keeps retrying indefinitely, which
            // is how a live recording survives a network blip. One that never
            // opened gets the same budget and then stops, so a refusal the
            // retry cannot change does not loop for the rest of the session.
            socket.close();
          }
          logger.debug(
            `WebSocket connection closed. event.code: ${ev.code}, event.reason: ${ev.reason}, event.wasClean: ${ev.wasClean}`
          );
          if (onStop) {
            onStop();
          }
        };

        // this happens when connection didn't establish / connection issues in the middle
        socket.onerror = (error) => {
          logger.error({
            event_type: 'RECORDING_SOCKET_ERROR',
            details: {
              sessionId: session_id,
              // A socket onerror delivers an Event, not an Error — log its type.
              errorName:
                error instanceof Error
                  ? error.name
                  : error instanceof Event
                    ? error.type
                    : typeof error,
            },
          });
          void dispatch.scribeRecordModel._markSocketError({
            sessionId: session_id,
          });
          if (onStop) {
            onStop();
          }
          return;
        };
      } catch (error) {
        let errorMessage =
          'Please check your browser permissions or your selected audio input';
        if (error instanceof DOMException) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        } else if (error instanceof Error) {
          errorMessage = error?.message;
        }
        jotaiStore.set(errorStartingRecordingAtom, () => {
          return {
            isOpen: true,
            error: errorMessage,
            shouldShowTroubleshootingGuide: true,
          };
        });
        logger.error({
          event_type: 'START_RECORDING_ERROR',
          details: {
            errorName: error instanceof Error ? error.name : typeof error,
            ...sanitizeError(error),
          },
        });

        if (onStop) {
          onStop();
        }

        throw error;
      }
    },

    _markSocketError(input: { sessionId: string }) {
      const key = GET_SESSION_BY_ID;
      // for offline
      logger.debug('marking socket error');
      reactQueryClient.setQueryData<QuerySessionResponse>(
        [key, { sessionId: input.sessionId }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }

          const newData: QuerySessionResponse = {
            ...oldData,
            isSocketError: true,
          };
          return newData;
        }
      );
    },

    async startChunkRecording(sessionId: string) {
      try {
        await dispatch.audio.startVolumeDetection();
        // void chunkAudioRecorder?.changeInputDevice();
        logger.debug('starting chunk recording: ' + sessionId);
        await chunkAudioRecorder?.start(sessionId);
      } catch (error) {
        logger.error({
          event_type: 'START_CHUNK_RECORDING_ERROR',
          details: {
            sessionId,
            errorName: error instanceof Error ? error.name : typeof error,
            ...sanitizeError(error),
          },
        });
        toast({
          tone: 'destructive',
          title: 'Something went wrong with starting your transcription',
          description: String(error) || 'Please try again later',
        });
        trackFailedToStartChunkRecording(String(error));
        dispatch.scribeRecordModel._stopOwnRecording({ sessionID: sessionId });
        reactQueryClient.setQueryData<SessionResponse>(
          [GET_SESSION_BY_ID, { sessionId: sessionId }],
          (old) => {
            if (!old) return undefined;
            return {
              ...old,
              recording_status: RecordingStatus.IDLE,
            };
          }
        );
      }
    },

    async startBackupOfflineRecordingV2() {
      const selectedAudioSource = store.getState().audio.selectedAudioSource;
      logger.debug('device label in backup: ' + selectedAudioSource?.label);
      await offlineAudioRecorder?.changeInputDevice(
        selectedAudioSource?.deviceId || 'default'
      );
      logger.debug(
        'st:off: ' + store.getState().scribeRecordModel.activeSocket.sessionID
      );
      await offlineAudioRecorder?.start();
    },

    async startBackupOfflineRecording() {
      const { isRunning } = store.getState().timerModel;

      if (!isRunning) {
        logger.debug('timer is not running, hence not starting backup recording');
        return;
      }

      await dispatch.scribeRecordModel.startBackupOfflineRecordingV2();
      return;
    },

    async handleSocketOpenV2() {
      const selectedAudioSource = store.getState().audio.selectedAudioSource;
      // change input device
      await audioRecorder?.changeInputDevice(selectedAudioSource?.deviceId || 'default');
      // keep sending to socket
      audioRecorder?.setOnDataAvailableHandler((data: Blob) => {
        const { socket, sessionID, lastReceivedHeartbeat, hasBeenMarkedError } =
          store.getState().scribeRecordModel.activeSocket;

        // logger.debug('sending size', data.size);
        if (socket?.readyState === WebSocket.OPEN) {
          //   logger.debug('sending data', data.size);
          // logger.debug('sending data');

          socket.send(data);

          if (lastReceivedHeartbeat && sessionID && !hasBeenMarkedError) {
            const now = Date.now();
            // if no heartbeat for 30 seconds, mark socket as error
            if (now - lastReceivedHeartbeat > 30000) {
              // socket is not responding
              dispatch.scribeRecordModel.setHasBeenMarkedError(true);
              dispatch.scribeRecordModel._markSocketError({
                sessionId: sessionID,
              });
            }
          }
        }
      });

      void audioRecorder?.start();
    },

    handleSocketOpen() {
      void dispatch.scribeRecordModel.handleSocketOpenV2();
    },

    regenerateNote(input: {
      sessionID: string;
      templateId?: string;
      aiCommandText?: string;
      aiCommandId?: string;
      generationMethod?: GenerationMethod;
      contentType?: ContentType;
      brain?: Brain;
      voiceStyle?: VoiceStyle;
      customStyle?: CustomStyle;
      isPersonalizationApplied?: boolean;
      isHaikuIntegration?: boolean;
      trigger?: NoteGenerateTrigger;
      onSuccess?: () => void;
    }) {
      // Belt: block regeneration once the transcript is deleted.
      if (isTranscriptDeletedInCache(input.sessionID)) {
        logger.warn('[scribeRecordModel] Blocked note regeneration: transcript deleted', {
          sessionId: input.sessionID,
        });
        return;
      }

      const sessionFromCache = reactQueryClient.getQueryData<QuerySessionResponse>([
        GET_SESSION_BY_ID,
        { sessionId: input.sessionID },
      ]);

      const generationMethod =
        input.generationMethod || sessionFromCache?.consult_note.generation_method;

      void dispatch.scribeRecordModel._streamResponse({
        sessionID: input.sessionID,
        templateId: input.templateId || sessionFromCache?.consult_note.template_id,
        aiCommandText:
          input.aiCommandText || sessionFromCache?.consult_note.ai_command_text,
        aiCommandId: input.aiCommandId || sessionFromCache?.consult_note.ai_command_id,
        generationMethod: generationMethod,
        contentType: input.contentType,
        voiceStyle:
          input.voiceStyle ||
          sessionFromCache?.consult_note.voice_style ||
          VoiceStyle.GOLDILOCKS,
        brain: input.brain || sessionFromCache?.consult_note.brain,
        dictationCleanupMode:
          sessionFromCache?.consult_note.dictation_cleanup_mode ||
          DictationCleanupModes.SMART,
        isRegenerate: true,
        customStyle:
          input.customStyle || sessionFromCache?.consult_note.custom_style || undefined,
        isPersonalizationApplied:
          input.isPersonalizationApplied ||
          sessionFromCache?.consult_note.is_personalization_applied ||
          undefined,
        isHaikuIntegration: input.isHaikuIntegration,
        trigger: input.trigger,
        onSuccess: input.onSuccess,
      });
    },

    streamAudioFile({ sessionID }: { sessionID: string }) {
      dispatch.scribeRecordModel._actionPostRecording({ sessionID });
    },

    streamAudioFromHaikuIntegration({
      sessionID,
      templateId: overrideTemplateId,
      aiCommandId: overrideAiCommandId,
      aiCommandText: overrideAiCommandText,
      trigger = 'explicit',
    }: {
      sessionID: string;
      trigger?: NoteGenerateTrigger;
      /** Template from the stop flow when we already know it (avoids reading a stale cache). */
      templateId?: string | null;
      aiCommandId?: string | null;
      aiCommandText?: string | null;
    }) {
      // Flag OFF: old path through shared desktop after-recording logic.
      if (!IS_HAIKU_NOTE_PICKER_DIRECT_DISPATCH_ENABLED()) {
        dispatch.scribeRecordModel._actionPostRecording({
          sessionID,
          isHaikuIntegration: true,
          generationMethod: GenerationMethod.TEMPLATE,
          transcribeMode: TranscribeMode.TRANSCRIBE,
          trigger,
        });
        return;
      }

      // Flag ON: start note writing here. Haiku still uses its own template
      // drawer before this runs; we skip the desktop picker path that can leave
      // the UI stuck on "generating" with no note job.
      const session = reactQueryClient.getQueryData<QuerySessionResponse>([
        GET_SESSION_BY_ID,
        { sessionId: sessionID },
      ]);

      const templateId = overrideTemplateId ?? session?.consult_note.template_id ?? null;
      const aiCommandId =
        overrideAiCommandId ?? session?.consult_note.ai_command_id ?? null;
      const aiCommandText =
        overrideAiCommandText ?? session?.consult_note.ai_command_text ?? null;

      if (!templateId && !aiCommandId && !aiCommandText) {
        logger.error('[HAIKU] missing template for note generation', {
          sessionId: sessionID,
        });
        updateConsultNoteStreamStatus(sessionID, StreamingStatus.NOT_STARTED);
        toast({
          tone: 'destructive',
          title: i18next.t('toast.templateSelectionRequired'),
          description: i18next.t('toast.selectTemplateBeforeGenerating'),
        });
        return;
      }

      logger.info('[HAIKU] dispatching note stream', {
        sessionId: sessionID,
        templateId,
        aiCommandId,
      });

      void dispatch.scribeRecordModel._streamResponse({
        sessionID,
        trigger,
        isHaikuIntegration: true,
        disableChunkRecording: true,
        shouldGenerateClinicalTasks: false,
        generationMethod: GenerationMethod.TEMPLATE,
        templateId,
        aiCommandId,
        aiCommandText,
        voiceStyle: session?.consult_note.voice_style ?? VoiceStyle.GOLDILOCKS,
        customStyle: session?.consult_note.custom_style ?? undefined,
        brain: session?.consult_note.brain,
        dictationCleanupMode:
          session?.consult_note.dictation_cleanup_mode ||
          getCurrentUser()?.session_settings?.default_dictation_cleanup_mode ||
          DictationCleanupModes.SMART,
      });
    },

    _stopOwnRecording({ sessionID }: { sessionID: string }) {
      dispatch.timerModel.onPause();
      dispatch.scribeRecordModel._stopAndCleanupMediaRecorder();
      const session = reactQueryClient.getQueryData<QuerySessionResponse>([
        GET_SESSION_BY_ID,
        { sessionId: sessionID },
      ]);
      const seconds = store.getState().timerModel.seconds;
      sessionStopRecording({
        sessionId: sessionID,
        duration: seconds,
        isTabSharing: session?.is_tab_sharing ?? false,
      });

      audioRecorder?.stop();
    },

    _actionPostRecording({
      sessionID,
      isHaikuIntegration,
      contentType,
      generationMethod,
      transcribeMode,
      trigger = 'autonomous',
    }: {
      sessionID: string;
      isHaikuIntegration?: boolean;
      contentType?: ContentType;
      generationMethod?: GenerationMethod;
      transcribeMode?: TranscribeMode;
      /** Post-stop generation is autonomous; an explicit button passes `explicit`. */
      trigger?: NoteGenerateTrigger;
    }) {
      // alert ('_actionPostRecording');
      const session = reactQueryClient.getQueryData<QuerySessionResponse>([
        GET_SESSION_BY_ID,
        { sessionId: sessionID },
      ]);

      const user = getCurrentUser();

      // An EMR-linked session must never silently auto-generate an Auto note
      // (see the post-transcription branch below). The cached session is
      // normalized via `normalizeSessionEhrProvider`, which folds
      // `patient_profile.ehr` into `ehr_provider`, so this single field already
      // reflects the session's EHR linkage.
      const isEhrLinkedSession = Boolean(session?.ehr_provider);

      const currentGenerationMethod =
        generationMethod || session?.consult_note.generation_method;

      // check if generation method is set and one of (aiCommandText or aiCommandId or templateId) is set
      const isTemplateSelected =
        session &&
        currentGenerationMethod &&
        (session?.consult_note.ai_command_text ||
          session?.consult_note.ai_command_id ||
          session?.consult_note.template_id);
      const defaultTemplateId = user?.session_settings?.default_template_id;
      const customStyle = session?.consult_note.custom_style;

      const currentVoiceStyle =
        session?.consult_note.voice_style ?? VoiceStyle.GOLDILOCKS;
      const currentBrain = session?.consult_note.brain;
      const currentTranscribeMode = transcribeMode || session?.transcribe_mode;

      if (currentTranscribeMode === TranscribeMode.DICTATE && session) {
        // start transcribe directly if dictate mode
        armDictateReengagementCard('recording');
        void dispatch.scribeRecordModel._streamResponse({
          sessionID,
          trigger,
          isHaikuIntegration,
          disableChunkRecording: isHaikuIntegration,
          shouldGenerateClinicalTasks: true,
          generationMethod: GenerationMethod.DICTATION,
          contentType,
          voiceStyle: currentVoiceStyle,
          customStyle: customStyle ?? undefined,
          brain: currentBrain,
          dictationCleanupMode:
            session.consult_note.dictation_cleanup_mode ||
            user?.session_settings?.default_dictation_cleanup_mode ||
            DictationCleanupModes.SMART,
        });
      } else if (isShadowHeidiEnabled() && !isHaikuIntegration) {
        // UKI-218: Shadow Heidi research trial — always prompt for a template
        // after stopping. The Note tab is hidden, so the picker is the only
        // template-selection surface; skip default-template auto-pick.
        // Reset the entry mode explicitly so a stale experiment mode can never
        // leak in if a previous close bypassed the modal's onOpenChange handler.
        jotaiStore.set(postTranscriptionEntryModeAtom, () => 'full');
        jotaiStore.set(isChooseNoteStructureModalOpenAtom, () => true);
      } else if (
        isTemplateSelected &&
        currentGenerationMethod !== GenerationMethod.AUTO_MODE
      ) {
        // Explicit per-session selection of a SPECIFIC template / AI command →
        // respected. Auto is intentionally NOT treated as an explicit bypass:
        // new users get Auto auto-applied from onboarding, so every Auto session
        // flows into the post-transcription experiment below instead of silently
        // auto-generating.
        void dispatch.scribeRecordModel._streamResponse({
          sessionID,
          trigger,
          isHaikuIntegration,
          disableChunkRecording: isHaikuIntegration,
          shouldGenerateClinicalTasks: !isHaikuIntegration,
          generationMethod: currentGenerationMethod,
          contentType,
          templateId: session.consult_note.template_id,
          aiCommandText: session.consult_note.ai_command_text,
          aiCommandId: session.consult_note.ai_command_id,
          voiceStyle: currentVoiceStyle,
          customStyle: customStyle ?? undefined,
          brain: currentBrain,
        });
      } else if (
        defaultTemplateId &&
        defaultTemplateId !== DEFAULT_TEMPLATE_AUTO_MODE &&
        session
      ) {
        // Specific (non-Auto) default template → respected. Experienced users who
        // configured a default template keep getting it and are NOT part of the
        // post-transcription note-entry experiment (GRO-3264/3265/3281).
        void dispatch.scribeRecordModel._streamResponse({
          sessionID,
          trigger,
          isHaikuIntegration,
          disableChunkRecording: isHaikuIntegration,
          shouldGenerateClinicalTasks: !isHaikuIntegration,
          generationMethod: currentGenerationMethod,
          contentType,
          templateId: defaultTemplateId,
          voiceStyle: currentVoiceStyle,
          customStyle: customStyle ?? undefined,
          brain: currentBrain,
        });
      } else {
        // Post-transcription "first note" decision point. The user would
        // otherwise get an Auto note — Auto is set on the session (auto-applied
        // for new users from onboarding, or explicitly chosen) or their saved
        // default is Auto — or, with no default at all, the template picker. This
        // whole population is the surface for the GRO-3264/3265/3281 A/B/C
        // experiment. Auto is folded in here on purpose: gating it out would
        // exclude the new-user audience the experiment targets.
        const wouldAutoGenerate =
          currentGenerationMethod === GenerationMethod.AUTO_MODE ||
          defaultTemplateId === DEFAULT_TEMPLATE_AUTO_MODE;
        const postTxVariant = GET_POST_TRANSCRIPTION_NOTE_ENTRY_VARIANT();
        const inPostTxExperiment =
          IS_POST_TRANSCRIPTION_NOTE_ENTRY_ASSIGNED() && !isHaikuIntegration && !!session;

        const openPostTxPicker = (mode: PostTranscriptionEntryMode) => {
          jotaiStore.set(postTranscriptionEntryModeAtom, () => mode);
          jotaiStore.set(isChooseNoteStructureModalOpenAtom, () => true);
        };

        const generateAutoNote = () => {
          void dispatch.scribeRecordModel._streamResponse({
            sessionID,
            trigger,
            isHaikuIntegration,
            disableChunkRecording: isHaikuIntegration,
            shouldGenerateClinicalTasks: true,
            generationMethod: GenerationMethod.AUTO_MODE,
            contentType,
            voiceStyle: currentVoiceStyle,
            customStyle: customStyle ?? undefined,
            brain: currentBrain,
          });
        };

        // An EMR-linked session must never silently auto-generate. Always
        // present the full picker so the clinician explicitly chooses a
        // template. This takes precedence over the post-transcription note-entry
        // experiment (the Auto option is gated off for EMR sessions, so the
        // auto_first arm would otherwise render an empty picker) and over a plain
        // Auto default. Placed before the exposure below so these users aren't
        // counted as exposed to an experience they never receive.
        if (isEhrLinkedSession) {
          // Flag ON safety net: Haiku note gen should use streamAudioFromHaikuIntegration.
          // If we land here anyway, don't open a picker the user can't see.
          if (isHaikuIntegration && IS_HAIKU_NOTE_PICKER_DIRECT_DISPATCH_ENABLED()) {
            logger.error(
              '[HAIKU] unexpected EHR picker path in _actionPostRecording — clearing streaming status',
              { sessionId: sessionID }
            );
            updateConsultNoteStreamStatus(sessionID, StreamingStatus.NOT_STARTED);
            return;
          }
          openPostTxPicker('full');
          return;
        }

        // First-note template selection for a genuine first-note user. This
        // pre-empts the post-transcription experiment on the first note only;
        // subsequent notes fall through to the experiment as normal. An
        // unresolved user cache is treated as not-first-note so a returning
        // user in the load window is never mis-targeted (which would also
        // suppress their normal note generation via the early return below).
        // Excludes Haiku the same way every other route out of this block
        // does — the modal isn't mounted in the Haiku host.
        if (
          IS_FIRST_NOTE_TEMPLATE_SELECTION_ENABLED() &&
          isFirstNoteUser(user) &&
          session &&
          !isHaikuIntegration
        ) {
          // Still return: falling through would expose this user to the
          // post-transcription experiment or a silent Auto note. Offline
          // recordings cannot generate yet; they also must not queue a picker
          // that would surface once the session syncs and the marker clears.
          if (!isOfflineLocalSession(session)) {
            jotaiStore.set(templateSelectionModalOpenAtom, () => true);
          }
          return;
        }

        // Manual Amplitude exposure for every assigned arm (incl. control) at the
        // moment the user is routed to their experience. The flag lives in
        // Flagsmith, which has no native Amplitude exposure, so this manual
        // $exposure is the experiment's only exposure signal.
        if (inPostTxExperiment) {
          amplitude.track('$exposure', {
            flag_key: POST_TRANSCRIPTION_NOTE_ENTRY_FLAG_KEY,
            variant: postTxVariant,
          });
        }

        if (inPostTxExperiment && postTxVariant === 'simple_picker') {
          openPostTxPicker('simple');
        } else if (inPostTxExperiment && postTxVariant === 'auto_first_picker') {
          openPostTxPicker('auto_first');
        } else if (inPostTxExperiment && postTxVariant === 'auto_generate') {
          generateAutoNote();
        } else if (wouldAutoGenerate && session) {
          // control / unassigned who would get an Auto note → unchanged behavior.
          generateAutoNote();
        } else {
          // control / unassigned with no default → unchanged behavior (full
          // picker). Reset mode so the standard picker shows.
          openPostTxPicker('full');
        }
      }
    },

    _stopExistingMediaRecorder() {
      const { ownMediaRecorder, mediaStream, androidBackgroundAudioPlayer } =
        store.getState().scribeRecordModel;
      if (ownMediaRecorder?.state !== 'inactive') {
        ownMediaRecorder?.stop();
      }
      mediaStream?.getTracks().forEach((track) => {
        track.stop();
        track.enabled = false;
      });
      // pause the play and clear the audio
      androidBackgroundAudioPlayer?.pause();
      androidBackgroundAudioPlayer?.remove();
    },

    _stopAndCleanupMediaRecorder() {
      const { socket } = store.getState().scribeRecordModel.activeSocket;
      dispatch.scribeRecordModel._stopExistingMediaRecorder();
      dispatch.scribeRecordModel.setOwnMediaRecorder(null);
      dispatch.scribeRecordModel.clearSocket();
      dispatch.scribeRecordModel.setMediaStream(null);
      socket?.close();

      void dispatch.audio.stopVolumeDetection();
    },

    /**
     * Stream-response entry point.
     *
     * The body lives in
     * `src/features/note-generation/stream-response/legacy/_stream-response.ts`
     * and is being refactored in place, one extracted concern per PR. There is
     * no parallel pipeline and no cutover flag.
     *
     * See docs/design-docs/stream-response-refactor-v4-in-place.md.
     */
    async _streamResponse(input: LegacyStreamResponseInput): Promise<void> {
      // Belt: every note path funnels through this shim, so one guard blocks
      // them all once the transcript is deleted. The UI owns the messaging
      // (disabled controls + toast); the model has no i18n, so it just logs.
      const transcriptDeleted = (): boolean => {
        if (!isTranscriptDeletedInCache(input.sessionID)) return false;
        logger.warn('[scribeRecordModel] Blocked note generation: transcript deleted', {
          sessionId: input.sessionID,
        });
        return true;
      };
      if (transcriptDeleted()) return;
      if (!isNoteGenerationRecoveryEnabled()) {
        await legacyStreamResponseBody(input);
        return;
      }
      const session = getSessionFromCache(input.sessionID);
      const baseline = session
        ? captureGenerationAttemptBaseline(session.consult_note)
        : undefined;
      await runWithAutoRetry({
        attempt: async (opts, n) => {
          if (n === 2 && transcriptDeleted()) {
            return { kind: 'skipped', reason: 'transcript-deleted' };
          }
          return legacyStreamResponseBody(
            n === 1 ? input : { ...input, trigger: 'autonomous' },
            { ...opts, baseline }
          );
        },
        readEnv: readAutoRetryEnv,
        commitFailed: (commit) => commitFailedGeneration(commit, { finish: true }),
        track: {
          decided: ({ attempt, outcome, decision }) =>
            noteAutoRetryDecided({
              session_id: input.sessionID,
              attempt,
              outcome: outcome.kind,
              error_code: outcome.kind === 'failed' ? outcome.code : undefined,
              decision: decision.kind,
              stop_reason: decision.kind === 'stop' ? decision.reason : undefined,
              is_empty: outcome.kind === 'completed' ? outcome.isEmpty : undefined,
            }),
        },
      });
    },

    /**
     * Handles post-note generation actions including clinical tasks, clinical codes panel setup, and active panel setting
     * This function is decoupled to be reusable in both normal note generation and side-by-side flows
     */
    handlePostNoteGenerationActions({
      sessionID,
      shouldGenerateClinicalTasks,
      user,
      isRegenerate,
      isHaikuIntegration,
    }: {
      sessionID: string;
      shouldGenerateClinicalTasks?: boolean;
      user?: ReturnType<typeof getCurrentUser>;
      isRegenerate?: boolean;
      isHaikuIntegration?: boolean;
    }) {
      // Generate content for queued/pinned documents after note generation completes.
      if (!isRegenerate && !isHaikuIntegration) {
        void (async () => {
          try {
            const documentsResponse = await getDocumentsBySessionId(sessionID);
            const existingDocuments = documentsResponse.data.documents || [];
            const docsToGenerateById = new Set<string>();

            // Generate content for any empty document created before transcription completed.
            for (const doc of existingDocuments) {
              if (!doc.content || doc.content.trim() === '') {
                const cachedDoc = reactQueryClient.getQueryData<QueryDocumentResponse>([
                  GET_DOCUMENT_BY_ID,
                  { sessionId: sessionID, documentId: doc.id },
                ]);
                if (cachedDoc?.streamingStatus !== StreamingStatus.MANUALLY_ABORTED) {
                  docsToGenerateById.add(doc.id);
                }
              }
            }

            // Existing pinned-template behavior (Pro users only).
            const currentUser = user || getCurrentUser();
            const isProUser = currentUser?.is_subscribed || false;
            if (isProUser) {
              const { data } = await getPinnedTemplates();
              const pinnedTemplates = data.templates;
              for (const template of pinnedTemplates ?? []) {
                const existingDocument = existingDocuments.find(
                  (doc) => doc.template_id === template.id
                );
                if (
                  existingDocument &&
                  (!existingDocument.content || existingDocument.content.trim() === '')
                ) {
                  docsToGenerateById.add(existingDocument.id);
                }
              }
            }

            const documentsListKey = [
              GET_DOCUMENTS_BY_ID,
              { sessionId: sessionID },
            ] as const;

            const sessionFromCache = getSessionFromCache(sessionID);
            const userBrain = mapNewBrain(
              currentUser?.session_settings?.brain,
              !isProUser
            );
            const userDefaultVoiceStyle =
              currentUser?.session_settings?.default_voice_style ?? undefined;
            const userDefaultCustomStyle =
              currentUser?.session_settings?.default_custom_style ?? undefined;
            const shouldApplyPersonalizationInsights = getPersonalizationPreference(
              {},
              sessionFromCache,
              currentUser
            );

            const generationPromises: Promise<void>[] = [];

            for (const documentId of docsToGenerateById) {
              const existingDocument = existingDocuments.find(
                (doc) => doc.id === documentId
              );
              if (!existingDocument) continue;

              reactQueryClient.setQueryData<GetDocumentsResponse>(
                documentsListKey,
                (oldData) => {
                  if (!oldData) return oldData;

                  const updatedDocuments = oldData.documents.map((doc) => {
                    if (doc.id === existingDocument.id) {
                      return {
                        ...doc,
                        streamingStatus: StreamingStatus.AWAITING_STREAMING_START,
                      };
                    }
                    return doc;
                  });

                  return { ...oldData, documents: updatedDocuments };
                }
              );

              const generationType = existingDocument.generation_type as DocumentGenType;
              if (!generationType || !DOCUMENT_GENERATION_TYPES.has(generationType))
                continue;

              if (existingDocument.template_id) {
                trackQueuedDocumentGenerationStarted({
                  session_id: sessionID,
                  template_id: existingDocument.template_id,
                  document_id: existingDocument.id,
                });
              }

              generationPromises.push(
                generateDocument({
                  sessionId: sessionID,
                  documentId: existingDocument.id,
                  templateId: existingDocument.template_id,
                  aiCommandId: existingDocument.ai_command_id,
                  aiCommandText: existingDocument.question,
                  generationMethod: generationType,
                  brain: userBrain,
                  voiceStyle: userDefaultVoiceStyle,
                  customStyle: userDefaultCustomStyle,
                  isPersonalizationApplied: shouldApplyPersonalizationInsights,
                  trigger: 'autonomous',
                })
                  .then(async () => {
                    await invalidateDocumentQuery({
                      sessionId: sessionID,
                      documentId: existingDocument.id,
                    });
                  })
                  .catch((error) => {
                    logger.error(
                      `Generation failed for queued/pinned document ${existingDocument.id}`,
                      {
                        errorName: error instanceof Error ? error.name : typeof error,
                        failureType: classifyDocumentGenerateError(error).name,
                        ...sanitizeError(error),
                      }
                    );

                    if (existingDocument.template_id) {
                      const errorCode = error instanceof Error ? error.name : undefined;
                      trackQueuedDocumentGenerationFailed({
                        session_id: sessionID,
                        template_id: existingDocument.template_id,
                        document_id: existingDocument.id,
                        error_code: errorCode,
                      });
                    }

                    reactQueryClient.setQueryData<GetDocumentsResponse>(
                      documentsListKey,
                      (oldData) => {
                        if (!oldData) return oldData;

                        const updatedDocuments = oldData.documents.map((doc) => {
                          if (doc.id === existingDocument.id) {
                            return {
                              ...doc,
                              streamingStatus: StreamingStatus.GENERATION_FAILED,
                            };
                          }
                          return doc;
                        });

                        return { ...oldData, documents: updatedDocuments };
                      }
                    );

                    reactQueryClient.setQueryData<QueryDocumentResponse>(
                      [
                        GET_DOCUMENT_BY_ID,
                        { sessionId: sessionID, documentId: existingDocument.id },
                      ],
                      (oldData) => {
                        if (!oldData) return oldData;
                        return {
                          ...oldData,
                          streamingStatus: StreamingStatus.GENERATION_FAILED,
                        };
                      }
                    );
                  })
              );
            }

            await Promise.all(generationPromises);

            await reactQueryClient.invalidateQueries({
              queryKey: [GET_DOCUMENTS_BY_ID, { sessionId: sessionID }],
            });

            // Session windows now use atoms - no query invalidation needed
          } catch (error) {
            logger.error(
              'Failed to generate content for queued/pinned documents:',
              error
            );
          }
        })();
      }

      // clinical tasks
      const userGenerateClinicalTasks = user?.session_settings?.generate_tasks ?? true;
      const userDefaultPanel =
        user?.session_settings?.default_side_panel_state ?? undefined;
      if (
        IS_CLINICAL_TASKS_ENABLED() &&
        userGenerateClinicalTasks &&
        shouldGenerateClinicalTasks
      ) {
        if (getIsInSessionNudgesChenmedEnabled()) {
          // ChenMed nudge sessions generate tasks server-side excluding protocol
          // actions the clinician already ticked off, instead of relying on the
          // (cohort-disabled) background autogen.
          void generateClinicalTasksWithExclusionsForSession(sessionID);
        } else if (!IS_TASK_GENERATION_BG_AUTOGEN_ENABLED()) {
          void generateClinicalTasksForSession(sessionID);
        } else {
          scheduleClinicalTasksRefetchAfterBackgroundAutogen(sessionID);
        }
      }
      if (getIsMedicationHighlightsEnabled()) {
        scheduleMedicationDetectionsRefetchAfterNoteComplete(sessionID);
      }
      if (IS_CLINICAL_CODING_ENABLED()) {
        jotaiStore.set(embeddingsPanelSessionIdAtom, sessionID);
      }
      // set the default panel atom;
      const session = getSessionFromCache(sessionID);
      // Keep the Tasks panel closed by default when the document-suggestion experiment, the
      // Evidence Central Search Bar, or ChenMed in-session protocol guidance is enabled. For
      // ChenMed the protocol widget is the clinician's task list, so auto-popping the
      // clinical-tasks panel post-generation would duplicate it — the panel stays available on
      // manual open (the Tasks toggle); it just doesn't open itself.
      let panelToOpen = userDefaultPanel;
      if (
        (GET_EVIDENCE_DOCUMENT_SUGGESTION_EXPERIMENT_VARIANT() !== 'control' ||
          IS_EVIDENCE_CENTRAL_SEARCH_BAR_ENABLED() ||
          getIsInSessionNudgesChenmedEnabled()) &&
        panelToOpen === SidePanelState.TASKS
      ) {
        panelToOpen = SidePanelState.CLOSED;
      }
      if (panelToOpen && session?.ehr_provider !== 'Healthdirect') {
        jotaiStore.set(activePanelAtom, panelToOpen);
      }
      if (session?.ehr_provider === 'Healthdirect') {
        // Close side panel because Healthdirect uses its own
        jotaiStore.set(activePanelAtom, SidePanelState.CLOSED);
      }

      void invalidateGamifyTrialExtension();

      void extractPatientProfileContextFromTranscript(sessionID);

      // A finalised note is the source the patient recap summarises, so
      // invalidate it for the linked profile (no-ops without an id),
      // otherwise the popover + drawer keep showing the previous summary
      // until a full page reload.
      invalidatePatientRecap(reactQueryClient, session?.patient_profile?.id);
      if (!session) {
        // Session evicted from cache between recording and post-note-gen is
        // extremely unlikely, but if it happens we silently skip the recap
        // refresh. Log it so the failure mode is observable instead of just
        // showing stale data on the next popover/drawer open.
        logger.warn('Could not invalidate patient recap: session not in cache', {
          sessionID,
        });
      }
    },
  }),
});
